import { CloseModalButton, Header, MobileHeading } from '@common/components/BookingForms';
import { useChildAges } from '@common/components/GuestPicker';
import { useRoomSettings } from '@common/features/roomSettings';
import { TranslationsFunc, injectTranslations } from '@lang';
import { ArrowLeftIcon } from '@root/styles/icons';
import { parseDateToString } from '@utils/helpers';
import { some } from '@utils/some';
import { isEqual } from 'lodash';
import moment from 'moment';
import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { Button } from '../../components/Button';
import { Filters, Modal } from './Filters';
import {
  AppliedFiltersRow,
  DesktopContainer,
  GuestHeadingContainer,
  MobileContainer,
  MobileInnerContainer,
  SeparatorWrapper,
} from './JourneyPicker.styles';
import type { JourneyPickerProps } from './JourneyPicker.types';
import { updateRoomsAfterCountChange } from './utils/updateRooms.utils';

const defaultJourneyPickerContainerProps: JourneyPickerProps = {
  journeyParams: {
    adults: 0,
    fromDate: '',
    children: 0,
    searchPhrase: '',
    spaDate: '',
    toDate: '',
    promoRateCode: '',
    childAges: {},
    rooms: [],
  },
  selectedCategory: {
    label: '',
    value: null,
  },
  onChange: () => {},
  showDatesPicker: true,
  t: () => '',
  rateCode: '',
  roomCode: '',
};

const hasDatesChanged = (currentDates, previousDates) =>
  !currentDates.fromDate.isSame(previousDates.fromDate) ||
  !currentDates.toDate.isSame(previousDates.toDate);

const hasCategoryChanged = (currentCategory, previousCategory) =>
  currentCategory?.value !== previousCategory?.value;

const hasGuestsChanged = (currentGuests, previousGuests) =>
  currentGuests.adults !== previousGuests.adults ||
  currentGuests.children !== previousGuests.children;

const hasRoomsChanged = (current, previous) => {
  return !isEqual(current.rooms, previous.rooms);
};

const JourneyPickerContainer = (
  props: JourneyPickerProps & { t: TranslationsFunc }
): JSX.Element => {
  const {
    journeyParams,
    selectedCategory: initialSelectedCategory,
    searchString: initialSearchString,
  } = props || defaultJourneyPickerContainerProps;

  const {
    onChange = () => {},
    showSearchInput,
    showDatesPicker = true,
    showCategoriesPicker,
    categories,
    showGuestsPicker,
    acceptPromoRateCode,
    t,
    rateCode,
    roomCode,
    fetchAvailability,
    userProperty,
    trackGaUpdate,
    isFetching,
    guestPickerValidationReset,
    shouldTriggerValidation = false,
  } = props;
  const [filters, setFilters] = useState({ ...journeyParams, searchPhrase: initialSearchString });

  useEffect(() => {
    setFilters({
      ...filters,
      selectedCategory: initialSelectedCategory,
    });
  }, [initialSelectedCategory]);

  const [hasPromoRateCodeChanged, setHasPromoRateCodeChanged] = useState(false);

  const { setSubmitted, onCloseGuestPicker, childAgesProps } = useChildAges({
    childAges: filters.childAges,
    childCount: filters.children,
    dispatchAction: (_, childAges) => {
      setFilters({ ...filters, childAges });
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGuestDetails, setEditingGuestDetails] = useState(false);

  function toggleEditGuestDetails(isEditing: boolean) {
    setEditingGuestDetails(isEditing);
  }

  useEffect(() => {
    if (shouldTriggerValidation) {
      setIsModalOpen(true);
      setSubmitted();
      if (guestPickerValidationReset) {
        guestPickerValidationReset();
      }
    }
  }, [shouldTriggerValidation, setSubmitted]);

  function toggleModal(isModalOpen) {
    setIsModalOpen(isModalOpen);
  }

  function handleApplyFilters(event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) {
    setSubmitted();

    if (childAgesProps?.errors.length) {
      event.stopPropagation();
      return;
    }

    const {
      fromDate,
      toDate,
      selectedCategory,
      adults,
      children,
      searchPhrase,
      promoRateCode,
      rooms,
    } = filters;

    const shouldUpdateDates = hasDatesChanged(filters, journeyParams);
    const shouldUpateGuests = hasGuestsChanged(filters, journeyParams);
    const shouldUpateRooms = hasRoomsChanged(filters, journeyParams);
    const shouldUpdateCategory = hasCategoryChanged(selectedCategory, initialSelectedCategory);
    const shouldUpdateAges =
      JSON.stringify(journeyParams.childAges) !== JSON.stringify(filters.childAges);

    if (
      shouldUpdateDates ||
      shouldUpateGuests ||
      shouldUpdateCategory ||
      showSearchInput ||
      shouldUpdateAges ||
      shouldUpateRooms ||
      hasPromoRateCodeChanged
    ) {
      const extendedDate = moment(journeyParams.fromDate);

      onChange({
        adults: (rooms || []).reduce((acc, room) => acc + room.adults, 0) ?? adults,
        children: (rooms || []).reduce((acc, room) => acc + room.children, 0) ?? children,
        ...(showDatesPicker && {
          fromDate: parseDateToString(fromDate),
          toDate: parseDateToString(toDate),
          spaDate: parseDateToString(fromDate),
        }),
        ...(searchPhrase && { filter: searchPhrase }),
        ...(showCategoriesPicker && { category: selectedCategory?.value }),
        promoRateCode,
        childAges: filters.childAges,
        rooms,
      });

      if (userProperty && fetchAvailability) {
        fetchAvailability(
          userProperty.id,
          {
            ...journeyParams,
            adults,
            children,
            childAges: filters.childAges,
            promoRateCode,
          },
          extendedDate,
          !!promoRateCode,
          rateCode ?? undefined,
          roomCode ?? undefined
        );
      }
    }

    trackGaUpdate?.({
      adults,
      children,
      fromDate: moment(fromDate),
      toDate: moment(toDate),
    });

    setHasPromoRateCodeChanged(false);
    toggleModal(false);
  }

  function handleCategoryChange(selectedCategory) {
    setFilters((filters) => ({
      ...filters,
      selectedCategory,
    }));
  }

  function handleDateChange(date) {
    const { startDate, endDate } = date;

    setFilters((filters) => ({
      ...filters,
      fromDate: startDate || filters.fromDate,
      toDate: endDate || filters.toDate,
    }));
  }

  function handleGuestCountChange(guest, count, roomNumber) {
    if (roomNumber && some(filters.rooms)) {
      const updatedRooms = filters.rooms?.map((room) =>
        room.roomNumber === roomNumber
          ? {
              ...room,
              [guest]: room[guest] + count,
            }
          : room
      );
      setFilters((filters) => ({
        ...filters,
        [guest]: filters[guest] + count,
        rooms: updatedRooms,
      }));
    } else {
      setFilters((filters) => ({
        ...filters,
        [guest]: filters[guest] + count,
        ...(filters.rooms &&
          some(filters.rooms) && {
            rooms: filters.rooms.map((room, index) =>
              index === 0 ? { ...room, [guest]: room[guest] + count } : room
            ),
          }),
      }));
    }
  }

  function handleRoomCountChange(roomNumber?: number) {
    const { updatedRooms, updatedAdults, updatedChildren } = updateRoomsAfterCountChange(
      rooms || [],
      roomSettings?.data.defaultParty,
      roomNumber
    );

    setFilters((filters) => ({
      ...filters,
      adults: updatedAdults,
      children: updatedChildren,
      rooms: updatedRooms,
    }));
  }

  function handleSearchPhraseChange(event) {
    const searchPhrase = event.target.value;

    setFilters((filters) => ({
      ...filters,
      searchPhrase,
    }));
  }

  function handlePromoRateCodeChange(promoRateCode) {
    setFilters((filters) => ({
      ...filters,
      promoRateCode,
    }));

    setHasPromoRateCodeChanged(true);
  }

  const {
    fromDate,
    toDate,
    selectedCategory,
    adults,
    children,
    searchPhrase,
    promoRateCode,
    rooms,
  } = filters;

  const childrenMessage = t('journey.children', { children });

  const { data: roomSettings } = useRoomSettings();

  return (
    <>
      <MobileContainer>
        <div>
          <Button onClick={() => toggleModal(!isModalOpen)} isSmall>
            {t('spaClasses.changeSearch')}
          </Button>
        </div>

        <AppliedFiltersRow>
          {showDatesPicker && (
            <li>
              <FormattedDate value={fromDate as string} month="short" day="2-digit" />
              <SeparatorWrapper>-</SeparatorWrapper>
              <FormattedDate value={toDate as string} month="short" day="2-digit" />
            </li>
          )}

          {showGuestsPicker && (
            <li>
              {t('spaClasses.adults', { adults })}
              {!roomSettings?.data.adultOnlyBookings && (
                <SeparatorWrapper>{childrenMessage}</SeparatorWrapper>
              )}
            </li>
          )}

          {selectedCategory && selectedCategory.label && (
            <li>
              {showDatesPicker || showDatesPicker}
              {selectedCategory.label}
            </li>
          )}
        </AppliedFiltersRow>

        <Modal isVisible={isModalOpen} increasedHeight>
          <Header>
            <MobileHeading>
              {editingGuestDetails ? (
                <GuestHeadingContainer onClick={() => setEditingGuestDetails(false)}>
                  <ArrowLeftIcon color="#20242e" height={'20px'} /> {t('common.guests')}
                </GuestHeadingContainer>
              ) : (
                t('spaClasses.filters')
              )}
            </MobileHeading>

            <CloseModalButton
              onClick={() => {
                if (editingGuestDetails) {
                  setEditingGuestDetails(false);
                } else {
                  toggleModal(false);
                }
              }}
            />
          </Header>

          <MobileInnerContainer>
            <Filters
              isFetching={isFetching}
              acceptPromoRateCode={acceptPromoRateCode}
              categories={categories}
              childAgesProps={childAgesProps}
              editingGuestDetails={editingGuestDetails}
              fromDate={fromDate}
              isInsideModal
              label={t('common.category')}
              numberOfAdults={adults}
              numberOfChildren={children}
              onApplyPromoRateCode={handlePromoRateCodeChange}
              onCategoryChange={handleCategoryChange}
              onCloseGuestPicker={onCloseGuestPicker}
              onDateChange={handleDateChange}
              onFiltersApply={handleApplyFilters}
              onGuestCountChange={handleGuestCountChange}
              onSearchPhraseChange={handleSearchPhraseChange}
              promoRateCode={promoRateCode}
              searchInputLabel={t('spaClasses.search')}
              searchPhrase={searchPhrase}
              selectedCategory={selectedCategory}
              showCategoriesPicker={showCategoriesPicker}
              showDatesPicker={showDatesPicker}
              showGuestsPicker={showGuestsPicker}
              showSearchInput={showSearchInput}
              submitLabel={t('spaClasses.update')}
              toDate={toDate}
              toggleEditGuestDetails={toggleEditGuestDetails}
              rateCode={rateCode}
              roomCode={roomCode}
              onRoomCountChange={handleRoomCountChange}
              rooms={rooms}
            />
          </MobileInnerContainer>
        </Modal>
      </MobileContainer>

      <DesktopContainer>
        <Filters
          isFetching={isFetching}
          acceptPromoRateCode={acceptPromoRateCode}
          categories={categories}
          childAgesProps={childAgesProps}
          fromDate={fromDate}
          label={t('common.category')}
          numberOfAdults={adults}
          numberOfChildren={children}
          onApplyPromoRateCode={handlePromoRateCodeChange}
          onCategoryChange={handleCategoryChange}
          onCloseGuestPicker={onCloseGuestPicker}
          onDateChange={handleDateChange}
          onFiltersApply={handleApplyFilters}
          onGuestCountChange={handleGuestCountChange}
          onSearchPhraseChange={handleSearchPhraseChange}
          promoRateCode={promoRateCode}
          searchInputLabel={t('spaClasses.search')}
          searchPhrase={searchPhrase}
          selectedCategory={selectedCategory}
          showCategoriesPicker={showCategoriesPicker}
          showDatesPicker={showDatesPicker}
          showGuestsPicker={showGuestsPicker}
          showSearchInput={showSearchInput}
          submitLabel={t('spaClasses.update')}
          toDate={toDate}
          rateCode={rateCode}
          roomCode={roomCode}
          onRoomCountChange={handleRoomCountChange}
          rooms={rooms}
        />
      </DesktopContainer>
    </>
  );
};

export const JourneyPicker = injectTranslations(JourneyPickerContainer);
